import { workflowsApi } from '@/api/workflows';

/**
 * localStorage key holding the workflow id the user is currently editing. Set when the
 * editor mounts and a lock is acquired; cleared when the user navigates back to the
 * Workflows list (AC08) or when the explicit release call returns.
 *
 * The key acts as a resilience net: if the user closes the tab, refreshes mid-edit, or
 * any other path skips the unmount handler, the next visit to the landing page reads
 * the orphan id and releases its lock before the 14h TTL expires. The release endpoint
 * itself is idempotent on the BE side — releasing a lock you don't hold is a no-op.
 */
export const EDITING_WORKFLOW_STORAGE_KEY = 'bmo-inflow.editingWorkflowId';

export function rememberEditingWorkflow(id: string): void {
  try {
    localStorage.setItem(EDITING_WORKFLOW_STORAGE_KEY, id);
  } catch {
    // Storage may be unavailable (private mode, quota); a missed reminder just means we
    // lean on the 14h TTL instead of the proactive release. Don't surface to the user.
  }
}

export function getRememberedEditingWorkflow(): string | null {
  try {
    return localStorage.getItem(EDITING_WORKFLOW_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function forgetEditingWorkflow(): void {
  try {
    localStorage.removeItem(EDITING_WORKFLOW_STORAGE_KEY);
  } catch {
    // Ignore — see rememberEditingWorkflow above.
  }
}

/**
 * Release whatever workflow is recorded in localStorage. Called from the Workflows
 * landing page on mount (AC08) so any orphan lock left over from a previous editor
 * session goes away as soon as the user returns to the list.
 *
 * Important: the local id is cleared AFTER the release attempt resolves successfully.
 * If we forget too early and the release fails (transient network blip, BE down),
 * the next landing-page visit has nothing to retry and the BE lock stays held until
 * TTL — bad for collaboration. Forget-on-success means a failed release leaves the
 * id around so the next mount can retry; the 14h TTL is the ultimate backstop.
 */
export async function releaseRememberedLockIfAny(): Promise<void> {
  const id = getRememberedEditingWorkflow();
  if (!id) return;
  try {
    await workflowsApi.releaseLock(id);
    forgetEditingWorkflow();
  } catch {
    // Retain the id so the next landing-page mount retries the release.
  }
}
